// ─────────────────────────────────────────────────────────────────────────────
// recognition_bridge.ts — Promise-based bridge to shape & spell Web Workers
//
// Falls back to synchronous main-thread execution when workers are
// unavailable (e.g. inside the VS Code webview sandbox).
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint } from '@shared/notebook_model';
import type { RecognisedShape } from '../../src/core/stroke_geometry';
import type { ClassificationResult } from '../../src/core/spell_classifier';
import type { AnalyseResponse, CandidatesResponse } from './shape_worker';
import type { ClassifyResponse, SerializedBlock } from './spell_worker';

// Sync fallbacks (bundled into the main app regardless — small overhead)
import { recogniseShape, recogniseShapeCandidates, isSimplifiable } from '../../src/core/stroke_geometry';
import { classifySpellFromStrokes } from '../../src/core/spell_classifier';

// ── Generic pending-request bookkeeping ──────────────────────────────────────

interface Pending<T> { resolve: (v: T) => void; reject: (e: Error) => void; }

let _nextId = 1;

function trySpawnWorker(url: string): Worker | null {
  try {
    const w = new Worker(url);
    // Test by waiting for any early error (CSP rejection is synchronous)
    let ok = true;
    w.onerror = () => { ok = false; };
    // Micro-task: if onerror fired synchronously, ok is already false
    if (!ok) { w.terminate(); return null; }
    return w;
  } catch {
    return null;
  }
}

// ── Shape recognition bridge ─────────────────────────────────────────────────

export interface ShapeAnalyseResult {
  simplifiable: boolean;
  shape: RecognisedShape | null;
}

export class ShapeBridge {
  private _worker: Worker | null;
  private _pending = new Map<number, Pending<unknown>>();

  constructor(workerUrl = './shape-worker.js') {
    this._worker = trySpawnWorker(workerUrl);
    if (this._worker) {
      this._worker.onmessage = (e: MessageEvent) => this._onMessage(e.data);
      this._worker.onerror = () => this._dropWorker();
    }
  }

  get offThread(): boolean { return this._worker !== null; }

  /** Full shape analysis: simplifiability check + optional recognition. */
  analyseShape(points: StrokePoint[], minConfidence: number): Promise<ShapeAnalyseResult> {
    if (!this._worker) {
      // Sync fallback
      const simplifiable = isSimplifiable(points);
      let shape: RecognisedShape | null = null;
      if (simplifiable) {
        const raw = recogniseShape(points);
        if (raw && raw.confidence >= minConfidence) shape = raw;
      }
      return Promise.resolve({ simplifiable, shape });
    }

    const id = _nextId++;
    this._worker.postMessage({ type: 'analyse', id, points, minConfidence });
    return new Promise<ShapeAnalyseResult>((resolve, reject) => {
      this._pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    });
  }

  /** Return top-N shape candidates (for evaluate-invocation popup). */
  recogniseCandidates(points: StrokePoint[], maxResults = 5): Promise<RecognisedShape[]> {
    if (!this._worker) {
      return Promise.resolve(recogniseShapeCandidates(points, maxResults));
    }

    const id = _nextId++;
    this._worker.postMessage({ type: 'candidates', id, points, maxResults });
    return new Promise<RecognisedShape[]>((resolve, reject) => {
      this._pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    });
  }

  dispose(): void {
    this._worker?.terminate();
    this._worker = null;
    this._rejectAll();
  }

  private _onMessage(msg: AnalyseResponse | CandidatesResponse): void {
    const p = this._pending.get(msg.id);
    if (!p) return;
    this._pending.delete(msg.id);

    if (msg.type === 'analyse') {
      p.resolve({ simplifiable: msg.simplifiable, shape: msg.shape } as ShapeAnalyseResult);
    } else {
      p.resolve(msg.shapes);
    }
  }

  private _dropWorker(): void {
    this._worker?.terminate();
    this._worker = null;
    this._rejectAll();
  }

  private _rejectAll(): void {
    for (const p of this._pending.values()) p.reject(new Error('Worker terminated'));
    this._pending.clear();
  }
}

// ── Spell classification bridge ──────────────────────────────────────────────

export class SpellBridge {
  private _worker: Worker | null;
  private _pending = new Map<number, Pending<ClassificationResult>>();

  constructor(workerUrl = './spell-worker.js') {
    this._worker = trySpawnWorker(workerUrl);
    if (this._worker) {
      this._worker.onmessage = (e: MessageEvent) => this._onMessage(e.data);
      this._worker.onerror = () => this._dropWorker();
    }
  }

  get offThread(): boolean { return this._worker !== null; }

  classify(
    strokes: StrokePoint[][],
    blocks: SerializedBlock[],
    pageId: string,
  ): Promise<ClassificationResult> {
    if (!this._worker) {
      // Sync fallback — reconstruct hitTest from serialized blocks
      const reversed = blocks.slice().reverse();
      const hitTest = (x: number, y: number) => {
        for (const b of reversed) {
          const pad = b.kind === 'figure' ? 8 : 0;
          if (x >= b.bbox.x - pad && x <= b.bbox.x + b.bbox.width + pad &&
              y >= b.bbox.y - pad && y <= b.bbox.y + b.bbox.height + pad) {
            return { id: b.id, kind: b.kind };
          }
        }
        return null;
      };
      return Promise.resolve(classifySpellFromStrokes(strokes, hitTest, pageId));
    }

    const id = _nextId++;
    this._worker.postMessage({ type: 'classify', id, strokes, blocks, pageId });
    return new Promise<ClassificationResult>((resolve, reject) => {
      this._pending.set(id, { resolve, reject });
    });
  }

  dispose(): void {
    this._worker?.terminate();
    this._worker = null;
    this._rejectAll();
  }

  private _onMessage(msg: ClassifyResponse): void {
    const p = this._pending.get(msg.id);
    if (!p) return;
    this._pending.delete(msg.id);
    p.resolve(msg.result);
  }

  private _dropWorker(): void {
    this._worker?.terminate();
    this._worker = null;
    this._rejectAll();
  }

  private _rejectAll(): void {
    for (const p of this._pending.values()) p.reject(new Error('Worker terminated'));
    this._pending.clear();
  }
}

export type { SerializedBlock } from './spell_worker';
